import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { MdAdd, MdDelete, MdEdit, MdUpload, MdSearch, MdAutoAwesome, MdLibraryBooks, MdOutlineContentCopy } from "react-icons/md";
import api from "@/services/api";
import { readFileAsync } from "@/utils";

const Knowledge = ({ answer, setAnswer }) => {
  const [knowledgeItems, setKnowledgeItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [extractingPdf, setExtractingPdf] = useState(false);
  const [newItem, setNewItem] = useState({
    label: "",
    content: "",
    tags: [],
    type: "manual"
  });

  useEffect(() => {
    if (answer?._id) {
      fetchKnowledge();
    }
  }, [answer]);

  const fetchKnowledge = async () => {
    try {
      setLoading(true);
      const { ok, data } = await api.post("/mission_ao_knowledge/search", {
        mission_ao_answer_id: answer._id
      });
      if (!ok) throw new Error("Failed to fetch knowledge");
      setKnowledgeItems(data);
    } catch (error) {
      console.error("Error fetching knowledge:", error);
      toast.error("Erreur lors du chargement des connaissances");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveKnowledge = async () => {
    try {
      if (!newItem.label || !newItem.content) {
        toast.error("Le titre et le contenu sont obligatoires");
        return;
      }

      setLoading(true);
      const payload = {
        ...newItem,
        mission_ao_answer_id: answer._id,
        mission_ao_answer_name: answer.name
      };

      let response;
      if (isEditing && selectedItem) {
        response = await api.put(`/mission_ao_knowledge/${selectedItem._id}`, payload);
      } else {
        response = await api.post("/mission_ao_knowledge", payload);
      }

      if (!response.ok) throw new Error("Failed to save knowledge");
      
      await fetchKnowledge();
      resetForm();
      toast.success(isEditing ? "Connaissance mise à jour" : "Connaissance ajoutée");
    } catch (error) {
      console.error("Error saving knowledge:", error);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteKnowledge = async (id) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cette connaissance ?")) return;
    
    try {
      setLoading(true);
      const { ok } = await api.remove(`/mission_ao_knowledge/${id}`);
      if (!ok) throw new Error("Failed to delete knowledge");
      
      await fetchKnowledge();
      if (selectedItem && selectedItem._id === id) {
        setSelectedItem(null);
      }
      toast.success("Connaissance supprimée");
    } catch (error) {
      console.error("Error deleting knowledge:", error);
      toast.error("Erreur lors de la suppression");
    } finally {
      setLoading(false);
    }
  };

  const handleEditKnowledge = (item) => {
    setSelectedItem(item);
    setNewItem({
      label: item.label,
      content: item.content,
      tags: item.tags || [],
      type: item.type || "manual"
    });
    setIsEditing(true);
    setShowAddModal(true);
  };

  const resetForm = () => {
    setNewItem({
      label: "",
      content: "",
      tags: [],
      type: "manual"
    });
    setIsEditing(false);
    setSelectedItem(null);
    setShowAddModal(false);
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      setExtractingPdf(true);
      toast.loading("Analyse du document en cours...");
      
      // Upload the file
      const rawBody = await readFileAsync(file);
      const { data: fileUrl } = await api.post(`/file`, { file: { rawBody, name: file.name }, folder: "knowledge" });
      
      // Extract content
      const { ok, data } = await api.post(`/mission_ao_knowledge/extract`, { url: fileUrl });
      if (!ok) throw new Error("Failed to extract content");
      
      toast.dismiss();
      toast.success("Document analysé avec succès");
      
      // Set the new item with extracted content
      setNewItem({
        ...newItem,
        label: file.name,
        content: data.summary,
        type: "ocr",
        url: fileUrl
      });
      
    } catch (error) {
      console.error("Error extracting PDF:", error);
      toast.dismiss();
      toast.error("Erreur lors de l'analyse du document");
    } finally {
      setExtractingPdf(false);
    }
  };

  const handleTagsChange = (e) => {
    const tagsString = e.target.value;
    const tagsArray = tagsString.split(',').map(tag => tag.trim()).filter(tag => tag);
    setNewItem({...newItem, tags: tagsArray});
  };

  const filteredItems = searchQuery 
    ? knowledgeItems.filter(item => 
        item.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.tags && item.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())))
      )
    : knowledgeItems;

  return (
    <div className="h-[calc(100vh-160px)] overflow-auto p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center text-white mr-3 shadow-md">
              <MdLibraryBooks className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Base de connaissances</h2>
              <p className="text-sm text-gray-500">Gérez vos connaissances pour améliorer les réponses de Claude</p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
              />
              <MdSearch className="absolute left-3 top-2.5 text-gray-400" size={20} />
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <MdAdd className="mr-1" /> Ajouter
            </button>
          </div>
        </div>

        {loading && knowledgeItems.length === 0 ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <MdLibraryBooks className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune connaissance disponible</h3>
            <p className="text-gray-500 max-w-md mx-auto mb-6">
              Ajoutez des connaissances pour améliorer les réponses de Claude en se basant sur vos réponses précédentes.
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <MdAdd className="mr-2" /> Ajouter une connaissance
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredItems.map((item) => (
              <div
                key={item._id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedItem(selectedItem && selectedItem._id === item._id ? null : item)}
              >
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-medium text-gray-900 truncate">{item.label}</h3>
                  <div className="flex">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditKnowledge(item);
                      }}
                      className="p-1 text-gray-500 hover:text-blue-600"
                    >
                      <MdEdit size={18} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteKnowledge(item._id);
                      }}
                      className="p-1 text-gray-500 hover:text-red-600"
                    >
                      <MdDelete size={18} />
                    </button>
                  </div>
                </div>
                
                <div className="text-sm text-gray-600 mb-3 line-clamp-3">{item.content}</div>
                
                {item.tags && item.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {item.tags.map((tag, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="flex items-center">
                    {item.type === "ocr" ? (
                      <MdAutoAwesome className="mr-1" size={14} />
                    ) : (
                      <MdLibraryBooks className="mr-1" size={14} />
                    )}
                    {item.type === "ocr" ? "Extrait par IA" : "Manuel"}
                  </span>
                  <span>{new Date(item.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedItem && (
          <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-medium text-gray-900">{selectedItem.label}</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(selectedItem.content);
                    toast.success("Contenu copié dans le presse-papier!");
                  }}
                  className="flex items-center p-2 text-sm font-medium text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100"
                >
                  <MdOutlineContentCopy className="mr-1" size={16} /> Copier
                </button>
                <button
                  onClick={() => handleEditKnowledge(selectedItem)}
                  className="flex items-center p-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
                >
                  <MdEdit className="mr-1" size={16} /> Modifier
                </button>
              </div>
            </div>
            
            <div className="prose prose-sm max-w-none overflow-auto bg-gray-50 rounded-lg p-4 text-gray-800">
              <p className="whitespace-pre-wrap">{selectedItem.content}</p>
            </div>
            
            {selectedItem.tags && selectedItem.tags.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Tags:</h4>
                <div className="flex flex-wrap gap-1">
                  {selectedItem.tags.map((tag, index) => (
                    <span key={index} className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal pour ajouter/modifier une connaissance */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {isEditing ? "Modifier la connaissance" : "Ajouter une nouvelle connaissance"}
              </h3>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Titre</label>
                  <input
                    type="text"
                    value={newItem.label}
                    onChange={(e) => setNewItem({...newItem, label: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Titre de la connaissance"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contenu</label>
                  <textarea
                    value={newItem.content}
                    onChange={(e) => setNewItem({...newItem, content: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[200px]"
                    placeholder="Contenu de la connaissance"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags (séparés par des virgules)</label>
                  <input
                    type="text"
                    value={newItem.tags.join(', ')}
                    onChange={handleTagsChange}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="technique, méthodologie, référence, ..."
                  />
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-800 mb-2 flex items-center">
                    <MdAutoAwesome className="mr-1" /> Extraction automatique
                  </h4>
                  <p className="text-sm text-blue-700 mb-3">
                    Importez un document PDF contenant des réponses précédentes pour que l'IA extraie automatiquement les connaissances pertinentes.
                  </p>
                  <label className="flex items-center justify-center w-full p-3 border border-blue-300 border-dashed rounded-lg cursor-pointer hover:bg-blue-100 transition-colors">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handlePdfUpload}
                      disabled={extractingPdf}
                      className="hidden"
                    />
                    <div className="flex items-center">
                      {extractingPdf ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500 mr-2"></div>
                          <span className="text-sm text-blue-700">Analyse en cours...</span>
                        </>
                      ) : (
                        <>
                          <MdUpload className="mr-2 text-blue-600" size={20} />
                          <span className="text-sm text-blue-700">Importer un PDF</span>
                        </>
                      )}
                    </div>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={resetForm}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveKnowledge}
                disabled={loading || !newItem.label || !newItem.content}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Enregistrement..." : isEditing ? "Mettre à jour" : "Ajouter"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Knowledge; 